export class EffectManager {
  /**
   * Apply a consumable item effect to a chocobo
   * @param {Object} chocobo - Target chocobo
   * @param {Object} item - Consumable item
   * @param {number} currentDay - Current game day
   * @returns {boolean} Success status
   */
  static applyConsumableEffect(chocobo, item, currentDay) {
    if (item.type !== 'consumable' || item.isUsed) {
      return false
    }

    // Initialize activeEffects if needed
    if (!chocobo.activeEffects) {
      chocobo.activeEffects = []
    }

    const effect = {
      id: crypto.randomUUID(),
      itemId: item.instanceId,
      itemName: item.name,
      effects: { ...item.effects },
      appliedDay: currentDay,
      expiresDay: item.effects.duration_months ? 
        currentDay + (item.effects.duration_months * 32) : null,
      type: item.effects.effect_type || 'unknown'
    }

    // Special handling for different effect types
    if (item.effects.effect_type === 'breeding_enhancement') {
      // Initialize consumedBreedingItems if needed
      if (!chocobo.consumedBreedingItems) {
        chocobo.consumedBreedingItems = []
      }
      
      chocobo.consumedBreedingItems.push({
        itemId: item.instanceId,
        itemName: item.name,
        effects: { ...item.effects },
        consumedDay: currentDay
      })
    } else if (item.effects.effect_type === 'permanent_enhancement') {
      // Handle permanent enhancements like Sylkis Greens
      this.applyPermanentEnhancement(chocobo, { ...item.effects, itemName: item.name }, currentDay)
    } else {
      // Add to active effects for training/XP boosts
      chocobo.activeEffects.push(effect)
    }

    // Mark item as used
    item.isUsed = true
    item.usedOnChocobo = chocobo.id
    item.usedOnDay = currentDay

    return true
  }

  /**
   * Equip a charm to a chocobo
   * @param {Object} chocobo - Target chocobo
   * @param {Object} item - Charm item
   * @returns {boolean} Success status
   */
  static equipCharm(chocobo, item) {
    if (item.type !== 'equipment' || item.category !== 'charm') {
      return false
    }

    // Initialize equipment if needed
    if (!chocobo.equipment) {
      chocobo.equipment = {
        charm: null
      }
    }

    // Unequip existing charm if any
    if (chocobo.equipment.charm) {
      this.unequipCharm(chocobo)
    }

    // Equip new charm
    chocobo.equipment.charm = {
      instanceId: item.instanceId,
      itemId: item.id,
      name: item.name,
      effects: { ...item.effects },
      equippedDay: Date.now()
    }

    return true
  }

  /**
   * Unequip a charm from a chocobo
   * @param {Object} chocobo - Target chocobo
   * @returns {boolean} Success status
   */
  static unequipCharm(chocobo) {
    if (!chocobo.equipment?.charm) {
      return false
    }

    chocobo.equipment.charm = null
    return true
  }

  /**
   * Calculate total XP multiplier from all active effects
   * @param {Object} chocobo - Target chocobo
   * @param {number} currentDay - Current game day
   * @returns {number} Total XP multiplier
   */
  static calculateXPMultiplier(chocobo, currentDay) {
    let multiplier = 1.0

    // Clean up expired effects first
    this.cleanupExpiredEffects(chocobo, currentDay)

    // Apply active consumable effects
    if (chocobo.activeEffects) {
      chocobo.activeEffects.forEach(effect => {
        if (effect.effects.xp_multiplier) {
          multiplier *= effect.effects.xp_multiplier
        }
      })
    }

    // Apply equipped charm effects
    if (chocobo.equipment?.charm?.effects?.xp_multiplier) {
      multiplier *= chocobo.equipment.charm.effects.xp_multiplier
    }

    return multiplier
  }

  /**
   * Calculate training multiplier for specific stat type
   * @param {Object} chocobo - Target chocobo
   * @param {string} statType - 'speed', 'stamina', or 'behavior'
   * @param {number} currentDay - Current game day
   * @returns {number} Training multiplier
   */
  static calculateTrainingMultiplier(chocobo, statType, currentDay) {
    let multiplier = 1.0

    // Clean up expired effects first
    this.cleanupExpiredEffects(chocobo, currentDay)

    const multiplierKey = `${statType}_training_multiplier`

    // Apply active consumable effects
    if (chocobo.activeEffects) {
      chocobo.activeEffects.forEach(effect => {
        if (effect.effects[multiplierKey]) {
          multiplier *= effect.effects[multiplierKey]
        }
        if (effect.effects.all_training_multiplier) {
          multiplier *= effect.effects.all_training_multiplier
        }
      })
    }

    // Apply equipped charm effects
    if (chocobo.equipment?.charm?.effects) {
      const charmEffects = chocobo.equipment.charm.effects
      if (charmEffects[multiplierKey]) {
        multiplier *= charmEffects[multiplierKey]
      }
      if (charmEffects.all_training_multiplier) {
        multiplier *= charmEffects.all_training_multiplier
      }
    }

    return multiplier
  }

  /**
   * Get breeding enhancement effects for a chocobo
   * @param {Object} chocobo - Parent chocobo
   * @returns {Object} Combined breeding effects
   */
  static getBreedingEffects(chocobo) {
    const effects = {
      perfect_inheritance: false,
      perfect_speed_inheritance: false,
      perfect_stamina_inheritance: false,
      perfect_behavior_inheritance: false,
      stat_bonus: 0,
      speed_bonus: 0,
      stamina_bonus: 0,
      behavior_bonus: 0,
      rare_color_boost: 0,
      gold_color_boost: 0,
      black_to_gold_boost: 0
    }

    // Check consumed breeding items
    if (chocobo.consumedBreedingItems) {
      chocobo.consumedBreedingItems.forEach(item => {
        Object.keys(effects).forEach(key => {
          if (item.effects[key]) {
            if (typeof effects[key] === 'boolean') {
              effects[key] = effects[key] || item.effects[key]
            } else {
              effects[key] += item.effects[key]
            }
          }
        })
      })
    }

    return effects
  }

  /**
   * Remove expired effects from a chocobo
   * @param {Object} chocobo - Target chocobo
   * @param {number} currentDay - Current game day
   */
  static cleanupExpiredEffects(chocobo, currentDay) {
    if (!chocobo.activeEffects) return

    chocobo.activeEffects = chocobo.activeEffects.filter(effect => {
      if (effect.expiresDay === null) return true // Permanent effect
      return currentDay < effect.expiresDay
    })
  }

  /**
   * Get all active effects for display
   * @param {Object} chocobo - Target chocobo
   * @param {number} currentDay - Current game day
   * @returns {Array} Active effects with display info
   */
  static getActiveEffectsDisplay(chocobo, currentDay) {
    const effects = []

    // Clean up expired effects first
    this.cleanupExpiredEffects(chocobo, currentDay)

    // Add active consumable effects
    if (chocobo.activeEffects) {
      chocobo.activeEffects.forEach(effect => {
        const daysRemaining = effect.expiresDay ? 
          Math.max(0, effect.expiresDay - currentDay) : null
        
        effects.push({
          name: effect.itemName,
          type: 'consumable',
          daysRemaining: daysRemaining,
          effects: effect.effects
        })
      })
    }

    // Add equipped charm effect
    if (chocobo.equipment?.charm) {
      effects.push({
        name: chocobo.equipment.charm.name,
        type: 'equipment',
        daysRemaining: null, // Permanent while equipped
        effects: chocobo.equipment.charm.effects
      })
    }

    // Add permanent enhancements
    if (chocobo.permanentEnhancements) {
      chocobo.permanentEnhancements.forEach(enhancement => {
        effects.push({
          name: enhancement.itemName,
          type: 'permanent',
          daysRemaining: null, // Permanently applied
          effects: enhancement.effects
        })
      })
    }

    return effects
  }

  /**
   * Apply permanent enhancements to a chocobo
   * @param {Object} chocobo - Target chocobo
   * @param {Object} effects - Enhancement effects
   * @param {number} currentDay - Current game day
   */
  static applyPermanentEnhancement(chocobo, effects, currentDay) {
    // Initialize permanent enhancements tracking if needed
    if (!chocobo.permanentEnhancements) {
      chocobo.permanentEnhancements = []
    }

    // Apply behavior improvement
    if (effects.behavior_improvement && chocobo.behaviorId > 1) {
      const targetBehavior = Math.max(1, chocobo.behaviorId - effects.behavior_improvement)
      chocobo.behaviorId = targetBehavior
    }

    // Apply permanent stat bonuses
    if (effects.speed_bonus) {
      chocobo.speedBonus += effects.speed_bonus
      chocobo.speed += effects.speed_bonus
    }

    if (effects.stamina_bonus) {
      chocobo.staminaBonus += effects.stamina_bonus
      chocobo.stamina += effects.stamina_bonus
    }

    // Update total stats after permanent changes
    if (typeof chocobo.updateTotalStats === 'function') {
      chocobo.updateTotalStats()
    }

    // Record the enhancement for tracking
    chocobo.permanentEnhancements.push({
      itemName: effects.itemName || 'Unknown Enhancement',
      effects: { ...effects },
      appliedDay: currentDay
    })
  }

  /**
   * Get permanent enhancements for display
   * @param {Object} chocobo - Target chocobo
   * @returns {Array} Permanent enhancements with display info
   */
  static getPermanentEnhancements(chocobo) {
    if (!chocobo.permanentEnhancements) return []
    
    return chocobo.permanentEnhancements.map(enhancement => ({
      name: enhancement.itemName,
      type: 'permanent',
      effects: enhancement.effects,
      appliedDay: enhancement.appliedDay
    }))
  }

  /**
   * Check if chocobo has any active effects
   * @param {Object} chocobo - Target chocobo
   * @param {number} currentDay - Current game day
   * @returns {boolean} Has active effects
   */
  static hasActiveEffects(chocobo, currentDay) {
    this.cleanupExpiredEffects(chocobo, currentDay)
    
    return (chocobo.activeEffects && chocobo.activeEffects.length > 0) ||
           (chocobo.equipment?.charm) ||
           (chocobo.permanentEnhancements && chocobo.permanentEnhancements.length > 0)
  }
}